import { action, makeObservable, observable, when } from 'mobx';
import { authService } from 'config/ioc';
import { isEmpty } from 'lodash';
import { FULFILLED, PENDING, REJECTED } from 'constants/actions';
import userStore from './UserStore';
import routerStore from './RouterStore';
import permissionsStore from './PermissionsStore';

type FetchState = typeof PENDING | typeof FULFILLED | typeof REJECTED;

export interface SignInData {
  username: string;
  password: string;
  [key: string]: unknown;
}

interface ReferrerLocation {
  pathname?: string;
}

interface RouterLocation {
  pathname?: string;
  state?: {
    from?: string | ReferrerLocation;
  };
}

interface RouterStore {
  history?: unknown;
  location?: RouterLocation;
  replace: (location: string | ReferrerLocation) => void;
}

class AuthStore {
  fetchState?: FetchState;

  isLoggedIn: string | boolean;

  constructor() {
    this.isLoggedIn = localStorage.getItem('isLoggedIn') || false;

    makeObservable(this, {
      fetchState: observable,
      isLoggedIn: observable,
      signIn: action.bound,
      signOut: action.bound,
    });

    when(
      () => Boolean(this.isLoggedIn && isEmpty(userStore.user)),
      () => {
        userStore.getUser().then(() => {
          permissionsStore.initialize().then(() => {
            this.checkAndRedirectIfNeeded();
          });
        });
      },
    );

    when(
      () =>
        Boolean(
          this.isLoggedIn &&
            !isEmpty(userStore.user) &&
            !permissionsStore.isInitialized,
        ),
      () => {
        permissionsStore.initialize().then(() => {
          this.checkAndRedirectIfNeeded();
        });
      },
    );
  }

  async signIn(data: SignInData): Promise<void> {
    this.fetchState = PENDING;

    try {
      await authService.signIn(data);

      this.isLoggedIn = true;
      localStorage.setItem('isLoggedIn', String(this.isLoggedIn));

      await userStore.getUser();

      await permissionsStore.refetch();

      this.fetchState = FULFILLED;

      this.redirectToReferrer();
    } catch (e) {
      this.fetchState = REJECTED;
      throw e;
    }
  }

  async signOut(): Promise<void> {
    this.fetchState = PENDING;

    try {
      await authService.signOut();
      localStorage.clear();
      this.isLoggedIn = false;
      this.fetchState = FULFILLED;
      const typedRouterStore = routerStore as unknown as RouterStore;
      if (typedRouterStore.location?.pathname !== '/signin') {
        typedRouterStore.replace('/signin');
      }
    } catch (e) {
      this.fetchState = REJECTED;
    }
  }

  checkAndRedirectIfNeeded = (): void => {
    const typedRouterStore = routerStore as unknown as RouterStore;
    const currentPath = typedRouterStore.location?.pathname;

    if (currentPath === '/') {
      const initialRoute = permissionsStore.getInitialRoute();

      if (typedRouterStore.history) {
        typedRouterStore.replace(initialRoute);
      }
    }
  };

  redirectToReferrer = (): void => {
    const typedRouterStore = routerStore as unknown as RouterStore;
    const referrerUrl = typedRouterStore.location?.state?.from;
    const referrerPathname =
      typeof referrerUrl === 'string' ? referrerUrl : referrerUrl?.pathname;

    if (typedRouterStore.history) {
      if (referrerUrl && referrerPathname !== '/') {
        typedRouterStore.replace(referrerUrl);
        return;
      }

      typedRouterStore.replace(
        permissionsStore.shouldRedirectToFinance ? '/finance' : '/',
      );
    }
  };
}

const authStore = new AuthStore();

export default authStore;
